import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useMemo } from "react";
import type { WorkflowPickerField } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useSpaces } from "../../hooks/useSpaces";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { Box } from "../Box";
import { CircleMesh } from "../CircleMesh";
import { parseBadgeConfig } from "../CircleMeshEditor";
import { ConnectorDropdown, RowButton } from "../ConnectorDropdown";
import { Icon } from "../Icon";
import { metaJoin } from "../MetaSeparator";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { App } from "./Form";

type Props = {
  item: WorkflowPickerField;
  app: App;
  disabled?: boolean;
};

const RowContainer = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

const RowInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const ChevronButton = styled("button")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: -2px;
  }
`;

const RowBadge = styled("span")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
`;

export function WorkflowPickerItem({ item, app, disabled }: Props) {
  const z = useZero();
  const [workflows] = useQuery(queries.allWorkflows());
  const [spaces] = useSpaces({ settingsView: true });
  const displayName = useSpaceDisplayName();
  const spaceNameMap = useMemo(
    () => new Map(spaces.map((s) => [s.id, displayName(s)])),
    [spaces, displayName]
  );

  const selectedId = app.backingWorkflowId ?? "";
  const selected = workflows.find((w) => w.id === selectedId);
  const sorted = [...workflows].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );
  const selectedBadge = parseBadgeConfig(
    selected?.badgeSeed ?? null,
    selected?.id ?? app.id
  );
  const selectedSubtitle = selected
    ? metaJoin([
        spaceNameMap.get(selected.spaceId),
        formatRelativeTime(selected.updatedAt),
      ])
    : "Workflow";

  const selectWorkflow = (w: (typeof workflows)[number]) => {
    const previousName = selected?.name ?? null;
    const isAutoNamed =
      !app.name || app.name === "Workflow" || app.name === previousName;
    if (isAutoNamed && w.name) {
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: app.id,
          name: w.name,
          identifier: app.identifier,
          urlPattern: app.urlPattern,
          configuration: app.configuration ?? {},
          ...(app.note != null && { note: app.note }),
        })
      );
    }
    z.mutate(
      sharedMutators.setWorkflowAppBacking({
        connectedAppId: app.id,
        workflowId: w.id,
      })
    );
  };

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <RowContainer>
        <span
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <CircleMesh
            key={selected?.badgeSeed ?? selected?.id}
            targetCircleSize={16}
            seed={selectedBadge.seed}
            mutingIntensity={selected ? 0 : 0.5}
            animate={false}
            restrictedColors={selectedBadge.colors}
          />
        </span>
        <RowInfo>
          <Text variant="text-regular-md" color="var(--text-primary)" ellipsis>
            {selected?.name ?? "Pick a workflow"}
          </Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {selectedSubtitle}
          </Text>
        </RowInfo>
        <ConnectorDropdown
          searchPlaceholder="Search workflows…"
          disabled={disabled}
          opener={
            <Tooltip content="Change workflow">
              <ChevronButton type="button" disabled={disabled}>
                <Icon
                  name="chevron-down"
                  size={16}
                  color="var(--text-secondary)"
                />
              </ChevronButton>
            </Tooltip>
          }
        >
          {(query, close) => {
            const lower = query.toLowerCase().trim();
            const filtered = lower
              ? sorted.filter((w) =>
                  (w.name ?? "").toLowerCase().includes(lower)
                )
              : sorted;
            if (filtered.length === 0) {
              return (
                <Box horizontal={8} vertical={8}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    No workflows found
                  </Text>
                </Box>
              );
            }
            return filtered.map((w) => {
              const badge = parseBadgeConfig(w.badgeSeed, w.id);
              const secondary = metaJoin([
                spaceNameMap.get(w.spaceId),
                formatRelativeTime(w.updatedAt),
              ]);
              return (
                <RowButton
                  key={w.id}
                  data-no-pill=""
                  onClick={() => {
                    selectWorkflow(w);
                    close();
                  }}
                >
                  <RowBadge>
                    <CircleMesh
                      key={w.badgeSeed ?? w.id}
                      targetCircleSize={8}
                      seed={badge.seed}
                      mutingIntensity={0}
                      animate={false}
                      restrictedColors={badge.colors}
                    />
                  </RowBadge>
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {w.name ?? "Untitled workflow"}
                    </Text>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {secondary}
                    </Text>
                  </div>
                  {w.id === selectedId && (
                    <Icon
                      name="check"
                      size={12}
                      color="var(--text-secondary)"
                    />
                  )}
                </RowButton>
              );
            });
          }}
        </ConnectorDropdown>
      </RowContainer>
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {item.helperText}
          </Text>
        </Box>
      )}
    </div>
  );
}
